import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/* What the learner's plan lets them work on in Hifz. Basic Hifz (Al-Fatihah, Ayat al-Kursi, Amana ar-Rasul, the first and last 10
   ayahs of Al-Kahf, Al-Mulk and the last 10 surahs) is in Noor and every package; Juz 'Amma and the later steps open with a package,
   the matching course and (from step 2) the Irtaqi add-on. The server enforces all of it on every call (backend/basic_hifz.py) -
   this only mirrors it so the screens can grey out and explain what is outside the plan instead of failing. */

const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";
const dashboardUrl = (page) => `${new URL(LOGIN_URL).origin}/dashboard${page ? `?page=${page}` : ""}`;
export const goToPlans = () => { window.location.href = dashboardUrl("plans"); };
export const goToApps = () => { window.location.href = dashboardUrl("apps"); };
// Deleting an account is done in one place, the HikmahVerses dashboard (My profile), which also removes the data in the apps.
export const goToDeleteAccount = () => { window.location.href = dashboardUrl("myprofile"); };

const TTL_MS = 30 * 1000;
let cache = null; // { at, data }
let inflight = null;

export function loadBasicHifz(force = false) {
  if (!force && cache && Date.now() - cache.at < TTL_MS) return Promise.resolve(cache.data);
  if (!inflight) {
    inflight = api.get("/basic-hifz").then((r) => { cache = { at: Date.now(), data: r.data }; return r.data; }).finally(() => { inflight = null; });
  }
  return inflight;
}

export function refreshBasicHifz() { return loadBasicHifz(true); }

export function useBasicHifz() {
  const [data, setData] = useState(cache?.data || null);
  useEffect(() => {
    let alive = true;
    loadBasicHifz().then((d) => alive && setData(d)).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Until the answer arrives nothing is locked (the server still refuses anything outside the plan).
  const loaded = !!data;
  const premium = loaded ? !!data.premium : true;   // anything above Basic Hifz is open
  const scope = data?.scope || {};
  const inScope = (s, a) => !loaded || (scope[String(s)] || []).some((r) => a >= r.from && a <= r.to);
  const rangeInScope = (s, a, b) => {
    if (!loaded) return true;
    for (let n = Math.min(a, b); n <= Math.max(a, b); n++) if (!inScope(s, n)) return false;
    return true;
  };
  const surahAllowed = (s) => !loaded || !!scope[String(s)];
  const allowedSurahs = Object.keys(scope).map(Number);
  // How many ayahs of a surah the plan lets the learner work on (null until the answer arrives).
  const scopeCount = (s) => (loaded ? (scope[String(s)] || []).reduce((n, r) => n + r.to - r.from + 1, 0) : null);
  const firstAyah = (s) => (loaded ? (scope[String(s)] || [])[0]?.from ?? 1 : 1);
  return { data, loading: !loaded, premium, inScope, rangeInScope, surahAllowed, firstAyah, allowedSurahs, basicSurahs: allowedSurahs, scopeCount };
}
